import { useEffect, useState } from 'react';
import { Link, Navigate, useNavigate } from 'react-router';
import Icon from '../components/Icon';
import AvatarEditor from '../components/AvatarEditor';
import { PRIMARY } from '../components/CaptureSheets';
import { copyText, qrDataUrl, shareLink } from '../lib/coach';
import { useRememberMode } from '../lib/mode';
import { useApp, useAppPath } from '../lib/AppContext';

const PLACEHOLDER = 'Bạn mới'; // User::PLACEHOLDER_NAME
const FIELD = 'input h-12 w-full rounded-[14px] border-line text-base';

// The dark top of both steps: the step, its progress bars, "Bỏ qua" (straight to coach mode) and the title.
function StepHeader({ step, title, text }) {
  const path = useAppPath();
  return (
    <header data-theme="macro-dark" className="pt-safe bg-base-200 text-base-content">
      <div className="mx-auto flex max-w-xl flex-col gap-3 px-4 pb-6 pt-4">
        <div className="flex min-h-11 items-center justify-between">
          <p className="text-xs font-bold uppercase tracking-wide text-muted">Chế độ HLV · Bước {step}/2</p>
          <Link to={path('/coach')} replace className="flex min-h-11 items-center px-1 text-sm font-bold text-[#6fd3a0]">Bỏ qua</Link>
        </div>
        <div className="grid grid-cols-2 gap-1.5" aria-hidden="true">
          {[1, 2].map((n) => <span key={n} className={`h-1 rounded-full ${n <= step ? 'bg-[#6fd3a0]' : 'bg-base-100'}`} />)}
        </div>
        <h1 className="text-[26px] font-extrabold leading-tight">{title}</h1>
        <p className="text-sm text-muted">{text}</p>
      </div>
    </header>
  );
}

// Both steps sit in coach mode (the personal prompts wait), and right after the role question coach mode is
// already on and opens first, so leaving here and coming back lands on the Học viên tab, not here again.
function Layout({ step, title, text, children }) {
  const { coach } = useApp();
  const path = useAppPath();
  useRememberMode();
  if (!coach) return <Navigate to={path('/coach/intro')} replace />;
  return (
    <div data-theme="macro" className="flex min-h-dvh flex-col bg-base-200 text-base-content">
      <StepHeader step={step} title={title} text={text} />
      <main className="pb-safe mx-auto flex w-full max-w-xl flex-1 flex-col gap-4 px-4 py-5">{children}</main>
    </div>
  );
}

// O3 "Học viên sẽ thấy bạn thế nào?": picture, name (students see "Coach <name>") and where they teach.
export function CoachProfileStep() {
  const { api, coach, setCoach, profile, setProfile } = useApp();
  const path = useAppPath();
  const navigate = useNavigate();
  const [name, setName] = useState(profile?.name && profile.name !== PLACEHOLDER ? profile.name : '');
  const [gym, setGym] = useState(coach?.gym || '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const next = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      // Saving a name also confirms it, so the name question doesn't come back in the personal app.
      if (name.trim() && (name.trim() !== profile?.name || profile?.needsName)) {
        const data = await api.patch('/profile', { name: name.trim() });
        setProfile((p) => ({ ...p, name: data.name, needsName: data.needs_name }));
      }
      if (gym.trim() !== (coach?.gym || '')) {
        const data = await api.patch('/coach', { gym: gym.trim() });
        setCoach((c) => ({ ...c, gym: data.gym }));
      }
      navigate(path('/coach/start/invite'));
    } catch (err) {
      setError(err.message);
      setBusy(false);
    }
  };

  return (
    <Layout step={1} title="Học viên sẽ thấy bạn thế nào?" text="Hiện khi học viên nhận lời mời hoặc nhập mã HLV của bạn.">
      <form className="flex flex-1 flex-col gap-4" onSubmit={next}>
        <section className="flex flex-col gap-4 rounded-[20px] bg-base-100 p-4">
          <div className="flex items-center gap-4">
            <AvatarEditor name={name || profile?.name} size={64} />
            <p className="text-sm font-bold text-brand-text">{profile?.avatar_url ? 'Đổi ảnh đại diện' : 'Thêm ảnh đại diện'}</p>
          </div>
          <label className="flex flex-col gap-1.5">
            <span className="text-sm font-semibold">Tên hiển thị</span>
            {/* Students see every coach as "Coach <name>", so "Coach" is fixed in front and the name is the account's. */}
            <span className={`${FIELD} flex items-center gap-1.5`}>
              <span className="font-semibold text-muted">Coach</span>
              <input aria-label="Tên của bạn" autoComplete="name" maxLength={50} className="min-w-0 flex-1 bg-transparent outline-none"
                placeholder="Minh" value={name} onChange={(e) => setName(e.target.value)} />
            </span>
          </label>
          <label className="flex flex-col gap-1.5">
            <span className="text-sm font-semibold">Nơi dạy</span>
            <input className={FIELD} maxLength={80} placeholder="VD: Gym Tân Bình" value={gym} onChange={(e) => setGym(e.target.value)} />
          </label>
        </section>
        <div className="mt-auto flex flex-col gap-2">
          {error && <p role="alert" className="text-sm font-medium text-error">{error}</p>}
          <button type="submit" className={PRIMARY} disabled={busy}>
            {busy && <span className="loading loading-spinner loading-sm" />} Tiếp tục
          </button>
        </div>
      </form>
    </Layout>
  );
}

function WayCard({ icon, dark, title, badge, text, onClick }) {
  return (
    <button type="button" onClick={onClick} className="flex min-h-16 w-full items-center gap-3 rounded-[20px] bg-base-100 p-4 text-left">
      <span className={`grid size-10 shrink-0 place-items-center rounded-[12px] ${dark ? 'bg-[#15261f] text-[#6fd3a0]' : 'bg-[#e3f1e9] text-[#17804f]'}`}>
        <Icon name={icon} className="size-5" />
      </span>
      <span className="min-w-0 flex-1">
        <span className="flex items-center gap-2 font-bold">
          {title}
          {badge && <span className="rounded-full bg-[#e3f1e9] px-2 py-0.5 text-[10px] font-bold uppercase text-[#17804f]">{badge}</span>}
        </span>
        <span className="block text-sm text-muted">{text}</span>
      </span>
      <Icon name="chevronRight" className="size-4 shrink-0 text-muted" />
    </button>
  );
}

// O4 "Mời học viên đầu tiên": the coach code made when coach mode came on, its QR, and the two ways to invite.
export function CoachInviteStep() {
  const { coach, native } = useApp();
  const path = useAppPath();
  const navigate = useNavigate();
  const [qr, setQr] = useState(null);
  const [status, setStatus] = useState(null);
  const url = coach ? `${native?.siteUrl || window.location.origin}/c/${coach.code}` : null;
  useEffect(() => { if (url) qrDataUrl(url).then(setQr).catch(() => setQr(null)); }, [url]);

  const share = async () => {
    const result = await shareLink({ title: 'Mã HLV Macro Mate', text: `Kết nối với mình trên Macro Mate, mã HLV: ${coach.code}`, url });
    setStatus(result === 'copied' ? 'Đã sao chép link' : null);
  };

  return (
    <Layout step={2} title="Mời học viên đầu tiên" text="Mã HLV của bạn đã sẵn sàng. Chọn cách mời phù hợp.">
      <section className="flex items-center gap-4 rounded-[20px] bg-base-100 p-4">
        {qr ? <img src={qr} alt={`Mã QR của mã HLV ${coach?.code}`} className="size-24 shrink-0 rounded-[12px]" /> : <div className="skeleton size-24 shrink-0 rounded-[12px]" />}
        <div className="min-w-0">
          <p className="text-xs font-bold uppercase tracking-wide text-muted">Mã HLV của bạn</p>
          <p className="tabular text-2xl font-extrabold tracking-wider">{coach?.code}</p>
          <button type="button" className="min-h-11 text-sm font-bold text-brand-text"
            onClick={async () => { await copyText(url); setStatus('Đã sao chép link'); }}>
            Sao chép link
          </button>
        </div>
      </section>
      {status && <p role="status" className="text-center text-sm font-medium text-brand-text">{status}</p>}
      <WayCard icon="qr" title="Chia sẻ mã HLV" badge="Miễn phí" text="Học viên tự nhập mã. Miễn phí, giữ gói của họ." onClick={share} />
      {/* The Học viên tab opens the invite sheet, or the seats sheet when there are no seats yet. */}
      <WayCard icon="gift" dark title="Tặng Pro qua lời mời" text="Mua chỗ, học viên bấm link là có Pro."
        onClick={() => navigate(path('/coach?invite=1'), { replace: true })} />
      <div className="mt-auto flex flex-col gap-2 pt-4">
        <Link to={path('/coach')} replace className={PRIMARY}>Vào trang Học viên</Link>
        <p className="text-center text-xs text-muted">Mời sau cũng được. Muốn tự theo dõi bản thân: bấm chip &quot;Chế độ HLV&quot; ở góc trên.</p>
      </div>
    </Layout>
  );
}
